import React, { FC, useState, useEffect } from 'react'
import axios from 'axios'
import { useOktaAuth } from '@okta/okta-react'
import { Link as RouteLink } from 'react-router-dom'
import { Typography } from '@mui/material'
import { sanitize } from 'dompurify'
import { app, routes, api, getAuthClient, oidcConfig } from 'config'
import { getDateInUTC, processErrorMsg, storage } from 'utils'
import {
  BaseLayout,
  Loader,
  Module,
  ModuleHeader,
  ModuleBody,
  ButtonContainer,
  CustomCheckbox,
  CustomButton,
} from 'components'
import useStyle from './subscription-agreement-style'

const SubscriptionAgreement: FC<{}> = () => {
  const [agreeToTermsAndCondition, setAgreeToTermsAndCondition] =
    useState(false)
  const [subscriptionAgreementHTML, setSubscriptionAgreementHTML] = useState('')
  const [showLoader, setShowLoader] = useState(false)
  const { classes } = useStyle()
  const { authState, oktaAuth } = useOktaAuth()

  const getSubscriberAgreementData = async () => {
    axios
      .get(api.subscriptionAgreement(), {
        headers: {
          'Content-Type': 'application/json',
        },
        withCredentials: false,
      })
      .then((response) => {
        setSubscriptionAgreementHTML(response.data.body)
      })
      .catch(() => {
        axios
          .get(api.subscriptionAgreementSecondary(), {
            headers: {
              'Content-Type': 'application/json',
            },
            withCredentials: false,
          })
          .then((response) => {
            setSubscriptionAgreementHTML(response.data.body)
          })
          .catch(() => {
            setShowLoader(false)
          })
      })
  }

  useEffect(() => {
    ;(async () => {
      setShowLoader(true)
      getSubscriberAgreementData()
      setShowLoader(false)
    })()
  }, [])

  const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setAgreeToTermsAndCondition(event.target.checked)
  }

  const handleAccept = async () => {
    const {
      payload: { sub: userId, preferred_username },
    } = oktaAuth.token.decode(authState.idToken.idToken)

    storage.setUser(preferred_username)

    const data: any = {
      userId,
      agreementAcceptedDate: getDateInUTC(),
    }

    try {
      setShowLoader(true)
      await axios.post(api.updateUserAgreementAcceptance(), data)

      const authClient = getAuthClient({
        clientId: oidcConfig.clientId,
        redirectUri: oidcConfig.redirectUri,
      })

      authClient.token.getWithRedirect({
        responseType: 'token',
        scopes: oidcConfig.scopes,
      })
    } catch (e: any) {
      setShowLoader(false)
      processErrorMsg(
        e?.response?.data || e,
        'Subscription Agreement',
        'User accepting Terms of Use',
      )
    }
  }

  return (
    <BaseLayout maxWidth={app.subscriptionAgreementWidth.layoutWidth}>
      <Loader visible={showLoader} />
      <Module>
        <ModuleHeader
          title="Bright MLS Subscription Agreement"
          children={undefined}
        />

        <ModuleBody maxWidth={app.subscriptionAgreementWidth.moduleWidth}>
          <Typography
            variant="body1"
            component="article"
            dangerouslySetInnerHTML={{
              __html: sanitize(subscriptionAgreementHTML),
            }}
            className={classes.content}
          />

          <CustomCheckbox
            marginTop={2}
            checked={agreeToTermsAndCondition}
            onChange={handleChange}
            label="I agree to the Terms and Conditions"
          />

          <ButtonContainer
            marginTop={1}
            primary={
              <CustomButton
                onClick={handleAccept}
                disabled={
                  !agreeToTermsAndCondition ||
                  showLoader ||
                  !subscriptionAgreementHTML
                }
              >
                Accept
              </CustomButton>
            }
            secondary={<RouteLink to={routes.logout}>Decline</RouteLink>}
          />
        </ModuleBody>
      </Module>
    </BaseLayout>
  )
}

export default SubscriptionAgreement
